<script>
import TreeNode from "./TreeNode.vue";

const ROOT_ID = "__tree_root__";

export default {
  name: "Tree",
  components: { TreeNode },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    modelValue: {
      type: [String, Number, null],
      default: null,
    },
    checkedIds: {
      type: Array,
      default: () => [],
    },
    nodeKey: {
      type: String,
      default: "id",
    },
    labelKey: {
      type: String,
      default: "label",
    },
    childrenKey: {
      type: String,
      default: "children",
    },
    showRoot: {
      type: Boolean,
      default: true,
    },
    rootLabel: {
      type: String,
      default: "",
    },
    rootId: {
      type: [String, Number],
      default: ROOT_ID,
    },
    showCheckbox: {
      type: Boolean,
      default: true,
    },
    defaultExpandAll: {
      type: Boolean,
      default: false,
    },
    defaultExpandRoot: {
      type: Boolean,
      default: true,
    },
    indent: {
      type: Number,
      default: 16,
    },
  },
  emits: ["update:modelValue", "update:checkedIds", "select", "check-change"],
  data() {
    return {
      expandedIds: [],
    };
  },
  computed: {
    rootLabelText() {
      return this.rootLabel || this.$t("Все");
    },
    treeData() {
      if (this.showRoot) {
        return [
          {
            [this.nodeKey]: this.rootId,
            [this.labelKey]: this.rootLabelText,
            [this.childrenKey]: this.items,
            __isRoot: true,
          },
        ];
      }
      return this.items;
    },
  },
  watch: {
    items: {
      immediate: true,
      handler() {
        if (this.defaultExpandRoot && this.showRoot) {
          if (!this.expandedIds.includes(this.rootId)) {
            this.expandedIds = [...this.expandedIds, this.rootId];
          }
        }
        if (this.defaultExpandAll) {
          this.expandedIds = this.collectAllIds(this.treeData);
        }
      },
    },
  },
  methods: {
    collectAllIds(nodes) {
      const ids = [];
      const walk = (list) => {
        list.forEach((n) => {
          const children = n?.[this.childrenKey];
          if (Array.isArray(children) && children.length) {
            ids.push(n[this.nodeKey]);
            walk(children);
          }
        });
      };
      walk(nodes);
      return ids;
    },
    onSelect(node) {
      const id = node?.[this.nodeKey];
      const value = node?.__isRoot ? null : id;
      this.$emit("update:modelValue", value);
      this.$emit("select", node?.__isRoot ? null : node);
    },
    onToggleExpand(id) {
      if (this.expandedIds.includes(id)) {
        this.expandedIds = this.expandedIds.filter((x) => x !== id);
      } else {
        this.expandedIds = [...this.expandedIds, id];
      }
    },
    onToggleCheck({ node, checked }) {
      const id = node?.[this.nodeKey];
      let next = [...this.checkedIds];
      if (checked) {
        if (!next.includes(id)) next.push(id);
      } else {
        next = next.filter((x) => x !== id);
      }
      this.$emit("update:checkedIds", next);
      this.$emit("check-change", { node, checked, checkedIds: next });
    },
    expandAll() {
      this.expandedIds = this.collectAllIds(this.treeData);
    },
    collapseAll() {
      this.expandedIds = this.showRoot ? [this.rootId] : [];
    },
  },
};
</script>

<template>
  <div class="tree">
    <TreeNode
      v-for="(node, idx) in treeData"
      :key="node[nodeKey]"
      :node="node"
      :depth="0"
      :parent-lines="[]"
      :is-last="idx === treeData.length - 1"
      :is-root="node.__isRoot === true"
      :node-key="nodeKey"
      :label-key="labelKey"
      :children-key="childrenKey"
      :selected-id="modelValue ?? (showRoot ? rootId : null)"
      :checked-ids="checkedIds"
      :expanded-ids="expandedIds"
      :show-checkbox="showCheckbox"
      @select="onSelect"
      @toggle-expand="onToggleExpand"
      @toggle-check="onToggleCheck"
    />
  </div>
</template>

<style scoped lang="scss">
.tree {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
</style>
